/**
 * Agent settings validation and sanitization against connector schemas.
 *
 * The `connector` table defines `agent_settings_schema` as the source of truth
 * for valid settings per connector type. These utilities ensure settings
 * are valid before persisting to the database.
 */

/**
 * Schema field definition from connector.agent_settings_schema
 */
export interface SchemaField {
  type: 'select' | 'slider' | 'number' | 'text' | 'toggle' | 'array';
  /**
   * For `type: 'text'` — the value must be an `https://` URL. Enforced lexically here
   * (RN-safe, no URL constructor); the backend connection-write seams additionally run
   * the SSRF blocklist (`validateWebhookUrl`) on these fields. Introduced for the API
   * channel's `webhook_url` — a plaintext `http:` endpoint would ship conversation
   * content in cleartext.
   *
   * `'git_checkout'` — a repository branch name or a full commit hash ({@link parseGitCheckout}),
   * refused at write otherwise (`validateTextFields`). Introduced for a hosted agent's
   * `repo_checkout`, which the vendor would otherwise refuse at the agent's first session.
   */
  format?: 'url' | 'git_checkout';
  /**
   * For `type: 'text'` — the most characters the value may hold. The agent writes of the
   * setup routes and CI push refuse a longer value (`validateTextFields`); an experiment
   * overlay and a connection's own settings are sanitized without it. A reader that uses the
   * value clips it to this bound again, or, for a `git_checkout` value, whose clipping would name
   * another branch, refuses it ({@link parseGitCheckout}).
   */
  maxLength?: number;
  required?: boolean;
  default?: unknown;
  options?: Array<string | { value: string; label: string; deprecated?: boolean }>;
  /** When true, accept any string value for select fields (not just values in options) */
  allowCustomValue?: boolean;
  min?: number;
  max?: number;
  /**
   * For `type: 'array'` — the element type (default `'text'`). Every element is a
   * string; `'select'` additionally constrains each element to `options` (unless
   * `allowCustomValue`). Introduced for the harness settings surface
   * (`skills[]`, `network_egress_allowlist[]`) in PR 2.1.
   */
  itemType?: 'text' | 'select';
  /** For `type: 'array'` — max element count (reject beyond). */
  maxItems?: number;
  /** For `type: 'array'` — min element count (an empty array passes unless set). */
  minItems?: number;
  dependsOn?: { field: string; values: string[] };
}

export type AgentSettingsSchema = Record<string, SchemaField>;

/** Lexical `https://<host>…` guard for `format: 'url'` fields (RN-safe — no URL constructor). */
const HTTPS_URL_LEXICAL = /^https:\/\/[^\s/]+/i;

/** What a repository checkout names: a branch, or one commit. */
export type GitCheckout = { type: 'branch'; name: string } | { type: 'commit'; sha: string };

/**
 * The most characters a `git_checkout` value may hold: its field's `maxLength`, and the bound
 * {@link parseGitCheckout} refuses past, so a value written by a path without the write guard
 * (an experiment overlay) is refused rather than clipped.
 */
export const GIT_CHECKOUT_MAX_LENGTH = 255;

/** A full commit hash: SHA-1 (40 hex) or SHA-256 (64 hex). A shorter one is read as a branch name. */
const FULL_COMMIT_HASH = /^(?:[0-9a-f]{40}|[0-9a-f]{64})$/i;

/**
 * Characters git never allows in a branch name (`git check-ref-format --branch`): ASCII
 * control characters, DEL, space, `~ ^ : ? * [ \`.
 */
// eslint-disable-next-line no-control-regex
const REF_FORBIDDEN_CHARS = /[\x00-\x20\x7f~^:?*[\\]/;

/**
 * Read a repository checkout setting (`format: 'git_checkout'`): a full commit hash is a commit
 * (lowercased), anything else a branch name under git's rules for one — no forbidden character,
 * no `..` or `@{`, not `@`, no leading `-`, no empty, `.`-led or `.lock`-ended path component, no
 * trailing `.`, at most {@link GIT_CHECKOUT_MAX_LENGTH} characters. Returns null for a value that
 * is neither. Lexical (RN-safe).
 */
export function parseGitCheckout(value: string): GitCheckout | null {
  if (value.length > GIT_CHECKOUT_MAX_LENGTH) return null;
  if (FULL_COMMIT_HASH.test(value)) return { type: 'commit', sha: value.toLowerCase() };
  if (!value || value === '@' || value.startsWith('-') || value.endsWith('.')) return null;
  if (REF_FORBIDDEN_CHARS.test(value) || value.includes('..') || value.includes('@{')) return null;
  const components = value.split('/');
  if (components.some((c) => c === '' || c.startsWith('.') || c.endsWith('.lock'))) return null;
  return { type: 'branch', name: value };
}

/**
 * Allowed string values for a select-style field. Shared by the `select` scalar
 * case and the `array` `itemType: 'select'` case — a plain-string option IS the
 * value; an object option carries `{ value }`.
 */
function optionValues(options: SchemaField['options']): string[] {
  return (options ?? []).map((opt) => (typeof opt === 'string' ? opt : opt.value));
}

/**
 * Validates agent_settings against the connector's schema.
 * Returns array of validation errors, or empty array if valid.
 * Use for strict validation paths (MCP, CI).
 */
export function validateAgentSettings(
  settings: Record<string, unknown>,
  schema: AgentSettingsSchema
): string[] {
  const errors: string[] = [];

  // Check required fields
  for (const [fieldName, fieldSchema] of Object.entries(schema)) {
    // Skip validation if field has dependsOn and condition is not met
    if (fieldSchema.dependsOn) {
      const dependsOnValue = settings[fieldSchema.dependsOn.field];
      if (!fieldSchema.dependsOn.values.includes(dependsOnValue as string)) {
        continue; // Field is not applicable for this configuration
      }
    }

    const value = settings[fieldName];

    // Check required
    if (fieldSchema.required && (value === undefined || value === null)) {
      errors.push(`Missing required field: ${fieldName}`);
      continue;
    }

    // Skip further validation if value not provided (and not required)
    if (value === undefined) continue;

    // Validate by type
    switch (fieldSchema.type) {
      case 'select': {
        if (typeof value !== 'string') {
          errors.push(`${fieldName} must be a string`);
        } else if (fieldSchema.options && !fieldSchema.allowCustomValue) {
          const allowedValues = optionValues(fieldSchema.options);
          if (!allowedValues.includes(value)) {
            errors.push(`Invalid value for ${fieldName}: "${value}". Allowed: ${allowedValues.join(', ')}`);
          }
        }
        break;
      }
      case 'slider':
      case 'number': {
        if (typeof value !== 'number') {
          errors.push(`${fieldName} must be a number`);
        } else {
          if (fieldSchema.min !== undefined && value < fieldSchema.min) {
            errors.push(`${fieldName} must be >= ${fieldSchema.min}`);
          }
          if (fieldSchema.max !== undefined && value > fieldSchema.max) {
            errors.push(`${fieldName} must be <= ${fieldSchema.max}`);
          }
        }
        break;
      }
      case 'text': {
        if (typeof value !== 'string') {
          errors.push(`${fieldName} must be a string`);
        } else {
          const invalid = textFieldError(fieldName, fieldSchema, value);
          if (invalid) errors.push(invalid);
        }
        break;
      }
      case 'toggle': {
        if (typeof value !== 'boolean') {
          errors.push(`${fieldName} must be a boolean`);
        }
        break;
      }
      case 'array': {
        if (!Array.isArray(value)) {
          errors.push(`${fieldName} must be an array`);
          break;
        }
        if (fieldSchema.maxItems !== undefined && value.length > fieldSchema.maxItems) {
          errors.push(`${fieldName} must have at most ${fieldSchema.maxItems} items`);
        }
        if (fieldSchema.minItems !== undefined && value.length < fieldSchema.minItems) {
          errors.push(`${fieldName} must have at least ${fieldSchema.minItems} items`);
        }
        const itemType = fieldSchema.itemType ?? 'text';
        // Every element must be a string; one message is enough (don't spam per item).
        if (value.some(item => typeof item !== 'string')) {
          errors.push(`${fieldName} items must be strings`);
        } else if (itemType === 'select' && fieldSchema.options && !fieldSchema.allowCustomValue) {
          const allowedValues = optionValues(fieldSchema.options);
          const bad = (value as string[]).find(item => !allowedValues.includes(item));
          if (bad !== undefined) {
            errors.push(`Invalid value in ${fieldName}: "${bad}". Allowed: ${allowedValues.join(', ')}`);
          }
        }
        break;
      }
    }
  }

  // Warn about unknown fields (not in schema)
  for (const fieldName of Object.keys(settings)) {
    if (!(fieldName in schema)) {
      errors.push(`Unknown field: ${fieldName}. This connector does not support this setting.`);
    }
  }

  // Cross-field rule: the system-prompt cache TTL must not be shorter-lived than
  // the conversation-history cache TTL (Anthropic rejects that ordering). Per-field
  // validation above can't express this, so check it here.
  const ttlError = validateCacheTtlOrder(settings);
  if (ttlError) errors.push(ttlError);

  return errors;
}

/**
 * A text value its field refuses, as an error, else null: one not of the field's `format`
 * (`url` is checked lexically: the backend seam runs the full SSRF blocklist), or one longer
 * than its `maxLength`.
 */
function textFieldError(fieldName: string, field: SchemaField, value: unknown): string | null {
  if (field.type !== 'text') return null;
  if (typeof value !== 'string') {
    // A checkout its reader would drop is refused: a number (YAML reads `3.12` as one) is not the
    // branch `3.12`. Absent or null is the field cleared. Other non-strings are not this guard's.
    const dropped = field.format === 'git_checkout' && value !== undefined && value !== null;
    return dropped ? `${fieldName} must be a branch name or a full commit hash` : null;
  }
  if (field.format === 'url' && !HTTPS_URL_LEXICAL.test(value)) return `${fieldName} must be an https:// URL`;
  if (field.maxLength !== undefined && value.length > field.maxLength) {
    return `${fieldName} must be at most ${field.maxLength} characters`;
  }
  // Empty is the field cleared: no checkout, so the repository's default branch.
  if (field.format === 'git_checkout' && value !== '' && !parseGitCheckout(value)) {
    return `${fieldName} must be a branch name or a full commit hash`;
  }
  return null;
}

/**
 * The write guard for `text` fields: the first text value its field refuses (its `format` or
 * its `maxLength`), as an error, else `null`. `sanitizeAgentSettings` keeps any value of a
 * known key, so the agent writes of the setup routes and CI push call this beside it, as they
 * call `validateCacheTtlOrder`.
 */
export function validateTextFields(
  settings: Record<string, unknown>,
  schema: AgentSettingsSchema,
): string | null {
  for (const [fieldName, fieldSchema] of Object.entries(schema)) {
    const invalid = textFieldError(fieldName, fieldSchema, settings[fieldName]);
    if (invalid) return invalid;
  }
  return null;
}

/**
 * Cross-field guard for Anthropic prompt-cache TTLs.
 *
 * The system-prompt breakpoint (`cache_ttl`) precedes the conversation-history
 * breakpoint (`cache_ttl_history`) in the prompt. Anthropic rejects a request where a
 * longer-lived cache breakpoint follows a shorter-lived one, so the single invalid
 * pairing is system `5m` + history `1h` (`off` removes a breakpoint, so it can never
 * violate the ordering; `1h`+`5m`, `5m`+`5m`, `1h`+`1h` are all fine).
 *
 * Returns an error string when the pairing is invalid, else `null`. Connector-agnostic:
 * only fires when BOTH keys carry the offending values, so it is a no-op for connectors
 * (OpenAI/Gemini/etc.) that never set these fields.
 *
 * NOTE: the runtime clamp in `resolveCacheTtls`
 * (apps/backend/src/llm/providers/cache-control.ts) REUSES this function to detect the bad
 * pairing and clamps it defensively for already-persisted rows — one source of truth.
 */
export function validateCacheTtlOrder(settings: Record<string, unknown>): string | null {
  if (settings.cache_ttl === '5m' && settings.cache_ttl_history === '1h') {
    return 'System Prompt Cache TTL (5m) must be ≥ Conversation History Cache TTL (1h): a longer-lived cache breakpoint cannot follow a shorter-lived one.';
  }
  return null;
}

/**
 * Result of sanitizing agent settings
 */
export interface SanitizeResult {
  /** Settings object with only schema-defined keys */
  sanitized: Record<string, unknown>;
  /** Keys that were stripped (not in schema) */
  strippedKeys: string[];
}

/**
 * Sanitizes agent_settings against the connector's schema.
 * Strips unknown keys not defined in the schema.
 * Use for lenient paths (REST API, CI, frontend).
 */
export function sanitizeAgentSettings(
  settings: Record<string, unknown>,
  schema: AgentSettingsSchema
): SanitizeResult {
  const sanitized: Record<string, unknown> = {};
  const strippedKeys: string[] = [];

  // Copy only schema-defined keys
  for (const key of Object.keys(settings)) {
    if (key in schema) {
      sanitized[key] = settings[key];
    } else {
      strippedKeys.push(key);
    }
  }

  return { sanitized, strippedKeys };
}
